import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError, TimeoutError } from 'rxjs';
import { catchError, map, switchMap, timeout, delay } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface VdiApproval {
  id: number;
  ticketId: number;
  ticketNumber: string;
  approvalStatus: string;
  approvedBy?: string;
  approvedDate?: string;
  approvalComments?: string;
  requesterEmail: string;
  requesterName?: string;
  serviceDescription?: string;
  softwareSet?: string;
  gpuRequirement: boolean;
  justification: string;
  remarks?: string;
  vmCreationStatus?: string;
  vmName?: string;
  vmSize?: string;
  hostName?: string;
  publicIpAddress?: string;
  privateIpAddress?: string;
  adminUsername?: string;
  adminPassword?: string;
  queueMessageId?: string;
  provisioningAttemptId?: string;
  requestedRegion?: string;
  requestedVmSize?: string;
  subscriptionId?: string;
  resourceGroupName?: string;
  vmCreationStartedAt?: string;
  vmCreationCompletedAt?: string;
  failureMessage?: string;
  failureCode?: string;
  createdDate: string;
  lastModifiedDate?: string;
}

export interface PendingApproval {
  id: number;
  ticketId: number;
  ticketNumber: string;
  requesterEmail: string;
  requesterName?: string;
  serviceDescription?: string;
  softwareSet?: string;
  gpuRequirement: boolean;
  justification: string;
  createdDate: string;
  remarks?: string;
}

export interface ApproveRejectRequest {
  comments?: string;
  vmSize?: string;
  region?: string;
}

export interface ApprovalResponse {
  message: string;
  approvalStatus?: string;
  approvedBy?: string;
  approvedDate?: string;
  rejectedBy?: string;
  rejectedDate?: string;
  vmCreationStatus?: string;
  vmName?: string;
  publicIpAddress?: string;
  queueMessageId?: string;
  provisioningAttemptId?: string;
  requestedVmSize?: string;
  requestedRegion?: string;
  subscriptionId?: string;
  resourceGroupName?: string;
}

export interface AzureVmStatus {
  vmName: string;
  powerState: string;  // "PowerState/running", "PowerState/stopped", "PowerState/deallocated"
  provisioningState: string;  // "Succeeded", "Failed"
  isRunning: boolean;
  isProvisioned: boolean;
  vmSize?: string;
  publicIpAddress?: string;
  privateIpAddress?: string;
  lastStatusUpdate: string;
}

export interface VmStatusResponse {
  approvalStatus: VdiApproval;
  vmStatus: AzureVmStatus | null;
  timestamp: string;
}

export interface VdiApprovalDetailsResponse {
  id?: number;
  ticketId: number;
  ticketNumber: string;
  approvalStatus: string;
  requesterEmail: string;
  requesterName?: string;
  serviceDescription?: string;
  softwareSet?: string;
  gpuRequirement: boolean;
  justification: string;
  remarks?: string;
  vmCreationStatus?: string;
  vmName?: string | null;
  vmSize?: string | null;
  publicIpAddress?: string | null;
  privateIpAddress?: string | null;
  approvedDate?: string;
  approvedBy?: string;
  rejectedDate?: string | null;
  rejectedBy?: string | null;
  rejectionReason?: string | null;
  createdDate: string;
  queueMessageId?: string | null;
  provisioningAttemptId?: string | null;
  requestedRegion?: string | null;
  requestedVmSize?: string | null;
  subscriptionId?: string | null;
  resourceGroupName?: string | null;
}

export interface ProvisioningStatusResponse {
  ticketId: number;
  ticketNumber: string;
  status: string;
  attemptNumber: number;
  vmSize?: string;
  vmName?: string;
  hostName?: string;
  startedUtc: string;
  completedUtc?: string;
  failureCode?: string;
  failureMessage?: string;
  hasConnectionDetails: boolean;
  publicIpAddress?: string;
  privateIpAddress?: string;
  correlationId?: string;
  resourceGroupName?: string;
  azureResourceId?: string;
  requestedRegion?: string;
  subscriptionId?: string;
  hostPoolName?: string;
  queueMessageId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class VdiApprovalService {
  private apiUrl = `${environment.apiEndpoint}api/vdi-approval`;

  // Event emitter for critical VM creation updates
  vdiStatusUpdate = new EventEmitter<any>();

  constructor(private http: HttpClient) {}

  /**
   * Get approval status for a ticket by ID
   */
  getApprovalStatus(ticketNumber: string): Observable<VdiApproval | null> {
    console.log(`🔍 [VDI Approval Service] Getting approval status for ticket ID: ${ticketNumber}`);
    console.log(`🌐 [VDI Approval Service] API URL: ${this.apiUrl}/status/${ticketNumber}`);
    console.log(`⏱️ [VDI Approval Service] Request timeout set to 30 seconds`);

    return this.http.get<VdiApproval>(`${this.apiUrl}/status/${ticketNumber}`).pipe(
      timeout(30000), // 30 second timeout
      map(response => {
        console.log(`✅ [VDI Approval Service] Got approval response:`, response);

        // Enhanced logging for VM creation status
        if (response) {
          console.log(`📋 [VDI Approval Service] Ticket ID: ${response.ticketId}`);
          console.log(`📄 [VDI Approval Service] Ticket Number: ${response.ticketNumber}`);
          console.log(`🔐 [VDI Approval Service] Approval Status: ${response.approvalStatus}`);
          console.log(`👤 [VDI Approval Service] Approved By: ${response.approvedBy}`);
          console.log(`🖥️ [VDI Approval Service] VM Creation Status: ${response.vmCreationStatus || 'NotStarted'}`);
          console.log(`📅 [VDI Approval Service] VM Creation Started: ${response.vmCreationStartedAt || 'Not started'}`);
          console.log(`✅ [VDI Approval Service] VM Creation Completed: ${response.vmCreationCompletedAt || 'Not completed'}`);
          console.log(`🏷️ [VDI Approval Service] VM Name: ${response.vmName || 'Not assigned'}`);
          console.log(`💾 [VDI Approval Service] VM Size: ${response.vmSize || 'Not specified'}`);
          console.log(`🌐 [VDI Approval Service] Public IP: ${response.publicIpAddress || 'Not assigned'}`);
          console.log(`🔒 [VDI Approval Service] Private IP: ${response.privateIpAddress || 'Not assigned'}`);
          console.log(`👨‍💼 [VDI Approval Service] Admin Username: ${response.adminUsername || 'Not assigned'}`);

          // 🚀 CRITICAL: Force UI update detection for successful VM creation
          if (response.vmCreationStatus === 'Succeeded' && response.vmName) {
            console.log('🎉 [VDI Approval Service] 🚨 CRITICAL UPDATE: VM Creation Succeeded! Forcing UI refresh...');
            console.log(`📊 [VDI Approval Service] VM Details: ${response.vmName} | ${response.vmSize} | IP: ${response.publicIpAddress || response.privateIpAddress}`);

            // Emit custom event to force UI components to update
            this.emitVdiStatusUpdate(response);
          }

          if (response.failureMessage) {
            console.log(`❌ [VDI Approval Service] Failure Message: ${response.failureMessage}`);
          }
          if (response.failureCode) {
            console.log(`🔍 [VDI Approval Service] Failure Code: ${response.failureCode}`);
          }
        }

        return response;
      }),
      catchError(error => {
        console.log(`❌ [VDI Approval Service] Error getting approval status for ticket ${ticketNumber}:`, error);
        
        // Handle timeout errors
        if (error instanceof TimeoutError || error.name === 'TimeoutError') {
          console.error(`⏱️ [VDI Approval Service] Request timed out after 30 seconds for ticket ${ticketNumber}`);
          return throwError(() => ({
            message: 'Request timed out - please check if the backend server is running',
            error: 'TimeoutError',
            status: 408
          }));
        }
        
        console.log(`📊 [VDI Approval Service] Error status:`, error.status);
        console.log(`📝 [VDI Approval Service] Error message:`, error.message);
        console.log(`🔍 [VDI Approval Service] Error details:`, JSON.stringify(error, null, 2));

        if (error.status === 404) {
          console.log(`⚠️ [VDI Approval Service] No approval record found for ticket ${ticketNumber} - returning null`);
          return of(null);
        }
        
        // Handle 403 Forbidden (authentication issue)
        if (error.status === 403) {
          console.error(`🔒 [VDI Approval Service] Authentication error - user not authorized`);
          return throwError(() => ({
            message: 'Authentication required - please log in again',
            error: 'Forbidden',
            status: 403
          }));
        }
        
        // Handle network errors
        if (error.status === 0) {
          console.error(`🌐 [VDI Approval Service] Network error - backend server may be unreachable`);
          console.error(`📍 [VDI Approval Service] Check if backend is running at: ${this.apiUrl}`);
          return throwError(() => ({
            message: 'Cannot connect to backend server - please check if the server is running',
            error: 'NetworkError',
            status: 0
          }));
        }
        
        return this.handleError('Failed to get approval status', error);
      })
    );
  }

  /**
   * Get approval status by ticket number
   */
  getApprovalStatusByTicketNumber(ticketNumber: string): Observable<VdiApproval | null> {
    console.log(`🔍 [VDI Approval Service] Getting approval status for ticket number: ${ticketNumber}`);
    console.log(`🌐 [VDI Approval Service] API URL: ${this.apiUrl}/status/by-ticket-no/${ticketNumber}`);
    
    return this.http.get<VdiApproval>(`${this.apiUrl}/status/by-ticket-no/${ticketNumber}`).pipe(
      timeout(30000), // 30 second timeout
      catchError(error => {
        // Handle timeout errors
        if (error instanceof TimeoutError || error.name === 'TimeoutError') {
          console.error(`⏱️ [VDI Approval Service] Request timed out after 30 seconds for ticket ${ticketNumber}`);
          return throwError(() => ({
            message: 'Request timed out - please check if the backend server is running',
            error: 'TimeoutError',
            status: 408
          }));
        }
        
        if (error.status === 404) {
          console.log(`⚠️ [VDI Approval Service] No approval record found for ticket ${ticketNumber}`);
          return of(null);
        }
        
        console.error(`❌ [VDI Approval Service] Error getting approval status for ticket ${ticketNumber}:`, error);
        return this.handleError('Failed to get approval status by ticket number', error);
      })
    );
  }

  /**
   * Approve a VDI ticket
   */
  approveTicket(ticketId: string, options?: { comments?: string; vmSize?: string; region?: string }): Observable<ApprovalResponse> {
    const request: ApproveRejectRequest = {
      comments: options?.comments,
      vmSize: options?.vmSize,
      region: options?.region
    };

    return this.http.post<ApprovalResponse>(`${this.apiUrl}/approve/${ticketId}`, request).pipe(
      catchError(error => this.handleError('Failed to approve ticket', error))
    );
  }

  /**
   * Reject a VDI ticket
   */
  rejectTicket(ticketId: number, comments?: string): Observable<ApprovalResponse> {
    const request: ApproveRejectRequest = { comments };

    return this.http.post<ApprovalResponse>(`${this.apiUrl}/reject/${ticketId}`, request).pipe(
      catchError(error => this.handleError('Failed to reject ticket', error))
    );
  }

  /**
   * Get pending VDI approvals for admin dashboard
   */
  getPendingApprovals(): Observable<PendingApproval[]> {
    return this.http.get<PendingApproval[]>(`${this.apiUrl}/pending`).pipe(
      catchError(error => this.handleError('Failed to get pending approvals', error))
    );
  }

  /**
   * Check if current user can approve tickets
   */
  canUserApproveTickets(): Observable<boolean> {
    const userEmail = this.getCurrentUserEmail();

    if (!userEmail) {
      console.warn('⚠️ [VDI Approval Service] No user email found when checking approval permissions.');
      return of(false);
    }

    const requestBody = { userEmail };

    return this.http.post<{ canApprove: boolean }>(`${this.apiUrl}/can-approve`, requestBody, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    }).pipe(
      map(response => response.canApprove),
      catchError(error => {
        // If user is not authenticated or doesn't have permission, return false
        if (error.status === 401 || error.status === 403) {
          return of(false);
        }
        return this.handleError('Failed to check approval permissions', error);
      })
    );
  }

  private getCurrentUserEmail(): string | null {
    try {
      const userRaw = localStorage.getItem('User');
      if (!userRaw) {
        return null;
      }

      const parsed = JSON.parse(userRaw);
      return parsed?.mail || parsed?.userPrincipalName || null;
    } catch (error) {
      console.warn('⚠️ [VDI Approval Service] Failed to parse user info from localStorage.', error);
      return null;
    }
  }

  /**
   * Refresh approval data for a specific ticket
   * Re-parses ticket detail to update approval record fields
   */
  refreshApprovalData(ticketId: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/refresh/${ticketId}`, {}).pipe(
      catchError(error => this.handleError('Failed to refresh approval data', error))
    );
  }

  /**
   * Refresh all stale approval records
   * Fixes approval records with unknown/default requester emails
   */
  refreshAllStaleApprovals(): Observable<{ message: string; refreshedCount: number }> {
    return this.http.post<{ message: string; refreshedCount: number }>(`${this.apiUrl}/refresh-all`, {}).pipe(
      catchError(error => this.handleError('Failed to refresh all approvals', error))
    );
  }

  /**
   * Get full approval details for a ticket
   */
  getApprovalDetails(ticketNumber: string): Observable<VdiApprovalDetailsResponse | null> {
    const url = `${this.apiUrl}/details/${ticketNumber}`;
    console.log(`🔍 [VDI Approval Service] Getting approval details for ticket: ${ticketNumber}`);
    console.log(`🌐 [VDI Approval Service] Approval details URL: ${url}`);

    return this.http.get<VdiApprovalDetailsResponse>(url).pipe(
      timeout(30000),
      map(response => {
        console.log(`✅ [VDI Approval Service] Received approval details:`, response);
        return response;
      }),
      catchError(error => {
        if (error instanceof TimeoutError || error.name === 'TimeoutError') {
          console.error(`⏱️ [VDI Approval Service] Approval details request timed out for ticket ${ticketNumber}`);
          return throwError(() => ({
            message: 'Request timed out - please check if the backend server is running',
            error: 'TimeoutError',
            status: 408
          }));
        }

        if (error.status === 404) {
          console.log(`⚠️ [VDI Approval Service] No approval details found for ticket ${ticketNumber}`);
          return of(null);
        }

        if (error.status === 0) {
          console.error(`🌐 [VDI Approval Service] Network error retrieving approval details`);
          return throwError(() => ({
            message: 'Cannot connect to backend server - please check if the server is running',
            error: 'NetworkError',
            status: 0
          }));
        }

        return this.handleError('Failed to get approval details', error);
      })
    );
  }

  /**
   * Check if a ticket is a VDI ticket (for UI display logic)
   */
  isVdiTicket(ticket: any): boolean {
    if (ticket.detail) {
      return ticket.detail.includes('Moodle VDI Request') ||
             ticket.detail.includes('VDI Request Details') ||
             ticket.detail.toLowerCase().includes('vdi request');
    }
    return false;
  }

  /**
   * Get approval status display information
   */
  getApprovalStatusInfo(approval: VdiApproval | null): {
    status: string;
    color: string;
    icon: string;
    description: string;
    canApprove: boolean;
    canReject: boolean;
  } {
    if (!approval) {
      return {
        status: 'No Approval Required',
        color: 'default',
        icon: 'info-circle',
        description: 'This ticket does not require VDI approval',
        canApprove: false,
        canReject: false
      };
    }

    switch (approval.approvalStatus) {
      case 'PendingApproval':
        return {
          status: 'Pending Approval',
          color: 'warning',
          icon: 'clock-circle',
          description: 'Waiting for admin approval',
          canApprove: true,
          canReject: true
        };

      case 'Approved':
        return {
          status: 'Approved',
          color: 'success',
          icon: 'check-circle',
          description: `Approved by ${approval.approvedBy || 'Unknown'}`,
          canApprove: false,
          canReject: false
        };

      case 'Rejected':
        return {
          status: 'Rejected',
          color: 'error',
          icon: 'close-circle',
          description: `Rejected by ${approval.approvedBy || 'Unknown'}`,
          canApprove: false,
          canReject: false
        };

      default:
        return {
          status: approval.approvalStatus,
          color: 'default',
          icon: 'info-circle',
          description: 'Unknown status',
          canApprove: false,
          canReject: false
        };
    }
  }

  /**
   * Get VM creation status display information
   */
  getVmCreationStatusInfo(approval: VdiApproval | null): {
    status: string;
    color: string;
    icon: string;
    description: string;
    progress?: number;
  } {
    if (!approval || !approval.vmCreationStatus) {
      return {
        status: 'Not Started',
        color: 'default',
        icon: 'minus-circle',
        description: 'VM creation not started'
      };
    }

    switch (approval.vmCreationStatus) {
      case 'InProgress':
        return {
          status: 'In Progress',
          color: 'processing',
          icon: 'sync',
          description: 'Azure VM is being created...',
          progress: 50
        };

      case 'Succeeded':
        return {
          status: 'Completed',
          color: 'success',
          icon: 'check-circle',
          description: `VM ${approval.vmName || 'Unknown'} created successfully`
        };

      case 'Failed':
        return {
          status: 'Failed',
          color: 'error',
          icon: 'exclamation-circle',
          description: approval.failureMessage || 'VM creation failed'
        };

      default:
        return {
          status: approval.vmCreationStatus,
          color: 'default',
          icon: 'info-circle',
          description: 'Unknown VM status'
        };
    }
  }

  /**
   * Emit VDI status update event to force UI refresh
   */
  emitVdiStatusUpdate(vdiApproval: VdiApproval): void {
    console.log('🚀 [VDI Approval Service] Emitting VDI status update event:', vdiApproval);
    this.vdiStatusUpdate.emit(vdiApproval);
  }

  /**
   * Get real-time Azure VM status for a ticket
   * Returns both approval status and live Azure VM power state
   */
  getAzureVmStatus(ticketNumber: string): Observable<VmStatusResponse> {
    const url = `${environment.apiEndpoint}vdi-monitoring/vm-status/${ticketNumber}`;
    console.log(`🔍 [VDI Approval Service] Getting Azure VM status for ticket: ${ticketNumber}`);
    console.log(`🌐 [VDI Approval Service] Azure VM status URL: ${url}`);

    return this.http.get<VmStatusResponse>(url).pipe(
      timeout(30000),
      map(response => {
        console.log(`✅ [VDI Approval Service] Got Azure VM status:`, response);
        
        if (response.vmStatus) {
          console.log(`🖥️ [Azure VM Status] VM Name: ${response.vmStatus.vmName}`);
          console.log(`⚡ [Azure VM Status] Power State: ${response.vmStatus.powerState}`);
          console.log(`🔄 [Azure VM Status] Provisioning State: ${response.vmStatus.provisioningState}`);
          console.log(`✅ [Azure VM Status] Is Running: ${response.vmStatus.isRunning}`);
          console.log(`📅 [Azure VM Status] Last Update: ${response.vmStatus.lastStatusUpdate}`);
        } else {
          console.log(`ℹ️ [Azure VM Status] No Azure VM status available (VM not created yet)`);
        }

        return response;
      }),
      catchError(error => this.handleError('Failed to get Azure VM status', error))
    );
  }

  /**
   * Get provisioning status for a ticket
   */
  getProvisioningStatus(ticketNumber: string): Observable<ProvisioningStatusResponse> {
    return this.http.get<ProvisioningStatusResponse>(`${environment.apiEndpoint}api/provisioning/status/${ticketNumber}`).pipe(
      catchError(error => this.handleError('Failed to get provisioning status', error))
    );
  }

  /**
   * Get Azure power state display information
   */
  getAzurePowerStateInfo(powerState: string): {
    status: string;
    color: string;
    icon: string;
    description: string;
  } {
    const state = powerState?.toLowerCase() || '';

    if (state.includes('running')) {
      return {
        status: 'Running',
        color: 'success',
        icon: 'play-circle',
        description: 'VM is currently running and accessible'
      };
    } else if (state.includes('stopped')) {
      return {
        status: 'Stopped',
        color: 'warning',
        icon: 'pause-circle',
        description: 'VM is stopped but still allocated'
      };
    } else if (state.includes('deallocated')) {
      return {
        status: 'Deallocated',
        color: 'default',
        icon: 'stop',
        description: 'VM is deallocated and not consuming resources'
      };
    } else if (state.includes('starting')) {
      return {
        status: 'Starting',
        color: 'processing',
        icon: 'loading',
        description: 'VM is starting up'
      };
    } else if (state.includes('stopping')) {
      return {
        status: 'Stopping',
        color: 'processing',
        icon: 'loading',
        description: 'VM is shutting down'
      };
    }

    return {
      status: 'Unknown',
      color: 'default',
      icon: 'question-circle',
      description: 'VM status is unknown'
    };
  }

  /**
   * Get RDP session URL for web-based RDP access
   */
  getRdpSessionUrl(ticketId: number): Observable<{ rdpUrl: string }> {
    console.log(`🖥️ [VDI Approval Service] Getting RDP session URL for ticket ${ticketId}`);

    // For now, return a mock URL - this would integrate with Guacamole or Azure Bastion
    return of({
      rdpUrl: `https://rdp.vtc.edu.hk/connect?ticket=${ticketId}&t=${Date.now()}`
    }).pipe(
      delay(1000), // Simulate API call
      map(response => {
        console.log('✅ [VDI Approval Service] RDP session URL generated:', response);
        return response;
      }),
      catchError(error => {
        console.error('❌ [VDI Approval Service] Failed to get RDP session URL:', error);
        return this.handleError('Failed to get RDP session URL', error);
      })
    );
  }

  private handleError(message: string, error: any): Observable<never> {
    console.error(`${message}:`, error);
    return throwError(() => ({
      message,
      error: error?.error || error?.message || 'Unknown error',
      status: error?.status
    }));
  }
}